/**
 * Util function to escape HTML.
 */
const replacementMap = Object.freeze({
    "&": "&amp;",
    '"': "&quot;",
    "<": "&lt;",
    ">": "&gt;",
});
const replacements = Object.freeze(Object.values(replacementMap));

/**
 * Make sure HTML entities are escaped.
 *
 * This is important to avoid script injection.
 *
 * This function is intended to be idempotent to simplify usage.
 */
export const escapeEntities = (input?: string | null): string => {
    if (input == null) {
        return "";
    }
    return input.replace(/([&<>"])(\w*;)?/g, (match, entity, tail) => {
        const replacement = replacementMap[entity];

        // If the replacement is null, the regex in our replace is picking up
        // a character it shouldn't be, but let's be graceful about it.
        // Check here to see if the match is one of our existing replacements
        // because if it is, then we don't want to replace it again.
        // @ts-expect-error - TS2345
        if (replacement == null || replacements.includes(match)) {
            return match;
        }

        // Have to return the replacement with the tail, if we captured one.
        return `${replacement}${tail || ""}`;
    });
};

const inverseReplacementMap = Object.freeze({
    "&amp;": "&",
    "&quot;": '"',
    "&lt;": "<",
    "&gt;": ">",
});
const inverseReplacements = Object.freeze(Object.values(inverseReplacementMap));

/** Unescape previously escaped entities */
export const unescapeEntities = (input?: string | null): string => {
    if (input == null) {
        return "";
    }
    return input.replace(
        /(&amp;|&lt;|&gt;|&quot;)(\w*;)?/g,
        (match, entity, tail) => {
            const replacement = inverseReplacementMap[entity];

            // If the replacement is null, the regex in our replace is picking up
            // a character it shouldn't be, but let's be graceful about it.
            // Check here to see if the match is one of our existing replacements
            // because if it is, then we don't want to replace it again.
            // @ts-expect-error - TS2345
            if (replacement == null || inverseReplacements.includes(match)) {
                return match;
            }

            // Have to return the replacement with the tail, if we captured one.
            return `${replacement}${tail || ""}`;
        },
    );
};
